import gsap from 'gsap'
import { debounce } from 'lodash'
import PropTypes from 'prop-types'
import React, { forwardRef, useCallback, useEffect, useRef } from 'react'
import { VideoPlayer, VideoWrapper } from './index.style'

const Video = forwardRef(
  (
    {
      src,
      width = 1920,
      height = 1080,
      background = true,
      playInView = true,
      autoplay,
    },
    ref
  ) => {
    const $wrapper = useRef(null)
    const $player = useRef(null)
    const aspectRatio = useRef(width / height)
    const playing = useRef(false)

    useEffect(() => {
      if (!background) return

      const handleResize = () => {
        $player.current.style.width = `max(100%, calc(${aspectRatio.current} * ${$wrapper.current.clientHeight}px))`

        $player.current.style.height = `max(100%, calc(${
          $wrapper.current.clientWidth
        }px * ${1 / aspectRatio.current}))`
      }

      const debouncedResizeHandler = debounce(handleResize, 200)

      handleResize()

      window.addEventListener('resize', debouncedResizeHandler)
    }, [background])

    // Only play background video when it is in view
    useEffect(() => {
      if (!playInView) return

      const play = () => {
        if (playing.current) return

        const playPromise = $player.current.play()

        if (playPromise !== undefined) {
          playPromise.then(() => {
            playing.current = true
          })
        }
      }

      const pause = () => {
        if (!playing.current) return

        $player.current.pause()
        playing.current = false
      }

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: $wrapper.current,
          start: 'top bottom',
          end: 'bottom top',
          onEnter: play,
          onEnterBack: play,
          onLeave: pause,
          onLeaveBack: pause,
        },
      })

      tl.to($player.current, {
        opacity: 1,
      })

      return () => {
        tl.kill()
      }
    }, [ref, playInView])

    const setRefs = useCallback(
      node => {
        $player.current = node

        if (ref) {
          ref.current = node
        }
      },
      [ref]
    )

    return (
      <VideoWrapper ref={$wrapper} background={background}>
        <VideoPlayer
          ref={setRefs}
          background={background}
          muted={background}
          loop={background}
          controls={!background}
          autoPlay={autoplay === false ? false : background}
          playsInline={true}
        >
          <source src={src} type="video/mp4" />
        </VideoPlayer>
      </VideoWrapper>
    )
  }
)

Video.displayName = 'Video'

Video.propTypes = {
  src: PropTypes.string.isRequired,
  background: PropTypes.bool,
}

export default Video
